import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const isAll = slug === 'all' || !slug;

  const filteredComics: Comic[] = useMemo(() => {
    let result = isAll ? [...comics] : comics.filter((c) => c.categorySlug === slug);
    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    return result;
  }, [slug, sortBy, isAll]);

  const categoryName = isAll ? '全部漫画' : `${currentCategory.name}漫画`;
  const categoryDesc = isAll
    ? '浏览韩漫画网全部韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑等多种分类。'
    : `韩漫画网${currentCategory.name}分类 - 精选优质${currentCategory.name}题材韩国漫画，免费在线阅读。`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 韩漫画网`,
    description: categoryDesc,
    url: `https://www.hanmanhua.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${categoryName} | 免费在线阅读韩漫`}
        description={categoryDesc}
        keywords={`韩漫画网,${currentCategory.name},韩国漫画,免费韩漫,在线韩漫`}
        canonicalUrl={`https://www.hanmanhua.com/category/${slug}`}
        structuredData={structuredData}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />

          {/* Header */}
          <div className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 <strong className="text-primary-500">{filteredComics.length}</strong> 部作品
                </p>
              </div>
              <div className="flex items-center gap-2">
                {[
                  { key: 'latest' as const, label: '最新', icon: 'ri-time-line' },
                  { key: 'popular' as const, label: '最热', icon: 'ri-fire-line' },
                  { key: 'rating' as const, label: '评分', icon: 'ri-star-line' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`flex items-center gap-1 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${opt.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 pb-6 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  (isAll && cat.slug === 'all') || cat.slug === slug
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Comic Grid */}
          <div className="pb-16">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {filteredComics.map((comic, idx) => (
                  <div
                    key={comic.id}
                    className="animate-fade-in-up"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <ComicCard comic={comic} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-book-2-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画作品</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}